import { Component, OnInit } from '@angular/core';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import {
  MedicalHistoryVisibilityApiConfig,
  MedicalHistoryVisibilityConfig,
  MedicalHistoryVisibilityKey,
  MedicalHistoryVisibilityPayload,
  MedicalHistoryVisibilityResponse,
} from './medical-history-visibility.models';

declare const $: any;

interface VisibilityOption {
  key: MedicalHistoryVisibilityKey;
  labelKey: string;
}

interface VisibilityGroup {
  titleKey: string;
  options: VisibilityOption[];
}

const VISIBILITY_KEYS: MedicalHistoryVisibilityKey[] = [
  'showBloodType',
  'showEmergencyContact',
  'showResidence',
  'showWorkInformation',
  'showHealthHabits',
  'showFamilyHistory',
  'showAllergies',
  'showConditions',
  'showMedications',
  'showProcedures',
  'showVaccines',
  'showAlerts',
];

const DEFAULT_VISIBILITY_CONFIG = VISIBILITY_KEYS.reduce((config, key) => {
  config[key] = true;
  return config;
}, {} as MedicalHistoryVisibilityConfig);

@Component({
  selector: 'app-medical-history-visibility-settings',
  templateUrl: './medical-history-visibility-settings.component.html',
  styleUrls: ['./medical-history-visibility-settings.component.scss'],
})
export class MedicalHistoryVisibilitySettingsComponent implements OnInit {
  loading = false;
  saving = false;
  loadError = false;
  saveError = false;
  config: MedicalHistoryVisibilityConfig | null = null;
  initialConfig: MedicalHistoryVisibilityConfig | null = null;

  readonly groups: VisibilityGroup[] = [
    {
      titleKey: 'MEDICAL_HISTORY_MEDICAL_INFORMATION',
      options: [
        { key: 'showBloodType', labelKey: 'MEDICAL_HISTORY_BLOOD_TYPE' },
        { key: 'showEmergencyContact', labelKey: 'MEDICAL_HISTORY_EMERGENCY_CONTACT' },
        { key: 'showResidence', labelKey: 'MEDICAL_HISTORY_RESIDENCE' },
        { key: 'showWorkInformation', labelKey: 'MEDICAL_HISTORY_WORK_INFORMATION' },
        { key: 'showHealthHabits', labelKey: 'MEDICAL_HISTORY_HEALTH_HABITS' },
      ],
    },
    {
      titleKey: 'MEDICAL_HISTORY_HISTORY',
      options: [
        { key: 'showFamilyHistory', labelKey: 'MEDICAL_HISTORY_FAMILY_HISTORY' },
        { key: 'showAllergies', labelKey: 'MEDICAL_HISTORY_ALLERGIES' },
        { key: 'showConditions', labelKey: 'MEDICAL_HISTORY_CONDITIONS' },
        { key: 'showMedications', labelKey: 'MEDICAL_HISTORY_MEDICATIONS' },
        { key: 'showProcedures', labelKey: 'MEDICAL_HISTORY_PROCEDURES' },
      ],
    },
    {
      titleKey: 'MEDICAL_HISTORY_OTHER_INFORMATION',
      options: [
        { key: 'showVaccines', labelKey: 'MEDICAL_HISTORY_VACCINATION' },
        { key: 'showAlerts', labelKey: 'MEDICAL_HISTORY_HEALTH_ALERTS' },
      ],
    },
  ];

  constructor(
    private lambdaService: LambdaService,
    private themingService: ThemingService
  ) {}

  ngOnInit(): void {
    void this.loadConfig();
  }

  get hasChanges(): boolean {
    if (!this.config || !this.initialConfig) {
      return false;
    }

    return VISIBILITY_KEYS.some((key) => this.config?.[key] !== this.initialConfig?.[key]);
  }

  async loadConfig(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    this.saveError = false;

    try {
      const response = await this.lambdaService.getMedicalHistoryVisibility(
        this.themingService.shopId
      ) as MedicalHistoryVisibilityResponse;

      if (!response?.success || !response.data) {
        throw new Error('Invalid medical history visibility response');
      }

      const normalized = this.normalizeConfig(response.data);
      this.config = this.cloneConfig(normalized);
      this.initialConfig = this.cloneConfig(normalized);
    } catch (error) {
      console.error('GET medical history visibility error', error);
      this.config = null;
      this.initialConfig = null;
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  setValue(key: MedicalHistoryVisibilityKey, checked: boolean): void {
    if (!this.config || this.saving) {
      return;
    }

    this.config[key] = checked;
    this.saveError = false;
  }

  cancelChanges(): void {
    if (!this.initialConfig || this.saving) {
      return;
    }

    this.config = this.cloneConfig(this.initialConfig);
    this.saveError = false;
  }

  async saveChanges(): Promise<void> {
    if (!this.config || !this.hasChanges || this.saving) {
      return;
    }

    this.saving = true;
    this.saveError = false;

    try {
      const response: any = await this.lambdaService.updateMedicalHistoryVisibility(
        this.themingService.shopId,
        this.toPayload(this.config)
      );

      if (!response?.success) {
        throw new Error('Medical history visibility update failed');
      }

      this.initialConfig = this.cloneConfig(this.config);
      this.openModal('#medicalHistoryVisibilitySuccessModal');
    } catch (error) {
      console.error('PUT medical history visibility error', error);
      this.saveError = true;
    } finally {
      this.saving = false;
    }
  }

  closeModal(): void {
    this.hideModal('#medicalHistoryVisibilitySuccessModal');
  }

  private normalizeConfig(data: MedicalHistoryVisibilityApiConfig): MedicalHistoryVisibilityConfig {
    return VISIBILITY_KEYS.reduce((config, key) => {
      const value = data[key];
      config[key] = value === undefined ? DEFAULT_VISIBILITY_CONFIG[key] : value === true || value === 1;
      return config;
    }, {} as MedicalHistoryVisibilityConfig);
  }

  private toPayload(config: MedicalHistoryVisibilityConfig): MedicalHistoryVisibilityPayload {
    return VISIBILITY_KEYS.reduce((payload, key) => {
      payload[key] = config[key] ? 1 : 0;
      return payload;
    }, {} as MedicalHistoryVisibilityPayload);
  }

  private cloneConfig(config: MedicalHistoryVisibilityConfig): MedicalHistoryVisibilityConfig {
    return { ...config };
  }

  private openModal(selector: string): void {
    if (typeof $ !== 'undefined') {
      $(selector).modal('show');
    }
  }

  private hideModal(selector: string): void {
    if (typeof $ !== 'undefined') {
      $(selector).modal('hide');
    }
  }
}

